import { memo } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import useStore from '../../store'
import { inactionMsg } from '../../lib/constraints'
import { fmtWeeks } from '../../lib/timeline'
import { CONFIG } from '../../config/config'

const STATUS_PILL = {
  active:       { label: 'Active',       cls: 'bg-m-accent/10 text-m-accent border-m-accent/20' },
  committed:    { label: 'Committed',    cls: 'bg-m-muted text-m-subtle border-m-border' },
  paused:       { label: 'Paused',       cls: 'bg-m-warn/10 text-m-warn border-m-warn/20' },
  aspirational: { label: 'Backlog',      cls: 'bg-m-muted text-m-faint border-m-border' },
  completed:    { label: 'Completed',    cls: 'bg-m-muted text-m-faint border-m-border' },
  archived:     { label: 'Archived',     cls: 'bg-m-muted text-m-faint border-m-border' },
}

const EFFORT_PILL = {
  1: { label: 'Low effort',    cls: 'text-m-subtle border-m-faint' },
  2: { label: 'Medium effort', cls: 'text-m-warn   border-m-warn/40' },
  3: { label: 'High effort',   cls: 'text-m-danger border-m-danger/40' },
}

const GoalCard = memo(function GoalCard({ goal }) {
  const profile        = useStore(s => s.profile)
  const activateGoal   = useStore(s => s.activateGoal)
  const pauseGoal      = useStore(s => s.pauseGoal)
  const resumeGoal     = useStore(s => s.resumeGoal)
  const completeGoal   = useStore(s => s.completeGoal)
  const backlogGoal    = useStore(s => s.backlogGoal)
  const afterMutation  = useStore(s => s.afterMutation)
  const constraints    = useStore(s => s.constraints)
  const phases         = useStore(s => s.phases.filter(p => p.goal_id === goal.id))

  const act = async (fn) => { await fn(goal.id); afterMutation() }

  const donePh   = phases.filter(p => p.status === 'completed').length
  const phasePct = phases.length ? (donePh / phases.length) * 100 : 0
  const inaction = inactionMsg(goal, profile?.weekly_available_hours)
  const totalH   = (goal.estimated_weeks ?? 0) * (goal.weekly_hours ?? 0)

  const pill    = STATUS_PILL[goal.status]  ?? STATUS_PILL.aspirational
  const eff     = EFFORT_PILL[goal.effort_level] ?? EFFORT_PILL[1]

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.18 }}
      className="card hover:border-m-dim transition-colors"
    >
      {/* ── Color accent bar ── */}
      {goal.color && (
        <div className="h-0.5 w-full" style={{ backgroundColor: goal.color }} />
      )}

      <div className="p-4 flex flex-col gap-3">

        {/* ── Title row ── */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <Link
              to={`/goals/${goal.id}`}
              className="font-mono text-sm text-m-text hover:text-m-accent transition-colors block"
            >
              {goal.title}
            </Link>
            {goal.category && (
              <span className="font-mono text-[10px] text-m-faint block mt-0.5">{goal.category}</span>
            )}
          </div>

          {/* Status pill */}
          <span className={`font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 border shrink-0 ${pill.cls}`}>
            {pill.label}
          </span>
        </div>

        {/* ── Metrics row ── */}
        <div className="flex items-center flex-wrap gap-2">
          <span className={`font-mono text-[10px] px-2 py-0.5 border ${eff.cls}`}>
            {eff.label}
          </span>
          {goal.estimated_weeks && (
            <span className="font-mono text-[10px] text-m-faint" title="Estimated duration">
              {fmtWeeks(goal.estimated_weeks)}
            </span>
          )}
          {goal.weekly_hours && (
            <span className="font-mono text-[10px] text-m-faint" title="Hours per week committed">
              {goal.weekly_hours}h/wk
            </span>
          )}
          {totalH > 0 && (
            <span className="font-mono text-[10px] text-m-faint" title="Total estimated effort">
              ~{totalH}h total
            </span>
          )}
        </div>

        {/* ── Phase progress bar ── */}
        {phases.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[9px] text-m-faint uppercase tracking-wider">Phases</span>
              <span className="font-mono text-[10px] text-m-faint">{donePh}/{phases.length} complete</span>
            </div>
            <div className="w-full h-1 bg-m-muted overflow-hidden">
              <motion.div
                className="h-full bg-m-accent"
                initial={{ width: 0 }}
                animate={{ width: `${phasePct}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </div>
          </div>
        )}

        {/* ── Inaction warning ── */}
        {inaction && (
          <div className="alert-info">
            <span className="font-mono text-[10px]">{inaction.msg}</span>
          </div>
        )}

        {/* ── Action buttons ── */}
        <div className="flex items-center gap-2 pt-0.5">
          {goal.status === 'aspirational' && (
            <button
              onClick={() => act(activateGoal)}
              disabled={constraints?.atCap}
              title={constraints?.atCap ? 'Active goal cap reached — complete or pause a goal first' : 'Move to active and allocate to your timeline'}
              className={`btn-sm ${constraints?.atCap ? 'btn-ghost opacity-40 cursor-not-allowed' : 'btn-accent-ghost'}`}
            >
              {constraints?.atCap ? 'Cap full' : 'Commit →'}
            </button>
          )}

          {goal.status === 'committed' && (
            <button
              onClick={() => act(activateGoal)}
              disabled={constraints?.atCap}
              className="btn-sm btn-accent-ghost"
            >
              Activate →
            </button>
          )}

          {goal.status === 'active' && (
            <>
              <button onClick={() => act(pauseGoal)} className="btn-sm btn-ghost">Pause</button>
              <button
                onClick={() => act(completeGoal)}
                className="btn-sm font-mono text-[10px] border border-m-accent/40 text-m-accent hover:bg-m-accent hover:text-m-bg transition-all px-3 py-1.5"
              >
                Complete ✓
              </button>
            </>
          )}

          {goal.status === 'paused' && (
            <>
              <button onClick={() => act(resumeGoal)} className="btn-sm btn-ghost">Resume</button>
              <button onClick={() => act(backlogGoal)} className="btn-sm btn-ghost">→ Backlog</button>
            </>
          )}

          {/* Always show details link */}
          <Link
            to={`/goals/${goal.id}`}
            className="font-mono text-[10px] text-m-faint hover:text-m-subtle transition-colors ml-auto"
          >
            View details →
          </Link>
        </div>
      </div>
    </motion.div>
  )
})

export default GoalCard
